/**
 * Builds a Markdown / plain-text "Vortex Device Kiosk Health Report" suitable
 * for pasting into WhatsApp, Slack, email, Notion, GitHub issues, or AI chats.
 *
 * Pure function — no React, no browser APIs, no clipboard calls, no network,
 * no side effects. Consumes already-computed Health-tab state (rows + health
 * results + compare result) so the builder remains deterministic and trivially
 * testable.
 *
 * Output uses an explicit allowlist of fields: anything not referenced here
 * cannot leak into the clipboard. No tokens, registration codes, API keys,
 * Bearer headers, passwords, Android ID, Build.SERIAL, raw signing cert, or
 * raw JSON dumps. Truncated `signing_fingerprint_short` is fine; full
 * digest never is.
 */

import { format } from 'date-fns';
import {
  type AppPermissionRow,
  type KioskCompareResult,
  type KioskHealthLabel,
  type KioskHealthResult,
  type KioskHealthV2Result,
  type KioskSchema,
  type KioskStateAny,
  type KioskV2SectionResult,
  type PackagePolicyRow,
  type RuntimeDriftResult,
  computeKioskHealth,
  computeRuntimeDrift,
  detectKioskSchema,
  healthLabelText,
  kioskFieldDisplays,
} from './kioskHealth';

// Subset shape of one update-log row from /api/admin/neptune/updates that the
// report builder actually reads. Keeping this typed and local avoids depending
// on the full admin API shape (and prevents accidental "include everything").
export interface KioskHealthReportRow {
  id?: string | number;
  checkedAt?: string;
  result?: string;
  vesselName?: string | null;
  deviceMeta?: {
    launcher_version?: string;
    android_version?: string;
    device_model?: string;
    kiosk_state?: KioskStateAny | null;
    [key: string]: unknown;
  };
  update_channel?: string;
  [key: string]: unknown;
}

/**
 * Strict allowlist of device fields the report may surface. Other Device
 * fields (registration_code, last_ip_address, device_fingerprint, etc.) are
 * intentionally excluded.
 */
export interface KioskHealthReportDeviceInfo {
  name?: string | null;
  serial_number?: string | null;
  la_version?: string | null;
  last_seen_at?: string | null;
}

export interface KioskHealthReportInput {
  generatedAt: Date;
  deviceId: string;
  device?: KioskHealthReportDeviceInfo | null;
  rows: KioskHealthReportRow[];
  latestCheckRow?: KioskHealthReportRow;
  latestSnapshotRow?: KioskHealthReportRow;
  isStaleSnapshot: boolean;
  schema: KioskSchema;
  latestState: KioskStateAny | null;
  health: KioskHealthResult;
  v2Result: KioskHealthV2Result | null;
  compare: KioskCompareResult | null;
}

// ─── Public API ─────────────────────────────────────────────────────────

export function buildKioskHealthReport(input: KioskHealthReportInput): string {
  const lines: string[] = [];

  lines.push(...buildHeader(input));
  lines.push('');
  lines.push(...buildDeviceBlock(input));
  lines.push('');
  lines.push(...buildLatestCheckBlock(input));
  lines.push('');

  if (input.schema === 'none' || input.latestState == null) {
    lines.push(...buildNoSnapshotBody(input));
    lines.push('');
    lines.push(...buildHistorySection(input.rows));
    lines.push(...buildFooter());
    return lines.join('\n');
  }

  lines.push(...buildLatestSnapshotBlock(input));
  lines.push('');
  lines.push(...buildStatusSection(input));
  lines.push('');

  const issues = buildIssuesSection(input);
  if (issues.length > 0) {
    lines.push(...issues);
    lines.push('');
  }

  const changes = buildChangesSection(input);
  if (changes.length > 0) {
    lines.push(...changes);
    lines.push('');
  }

  const runtimeDrift = buildRuntimeDriftSection(input);
  if (runtimeDrift.length > 0) {
    lines.push(...runtimeDrift);
    lines.push('');
  }

  if (input.schema === 'v2' && input.v2Result) {
    lines.push(...buildSectionSummaryTable(input.v2Result, input.compare ?? null));
    lines.push('');
    lines.push(...buildAppPermissionsSection(input.v2Result));
    lines.push('');
    lines.push(...buildPackagePolicySection(input.v2Result));
    lines.push('');
    lines.push(...buildFullSnapshotV2(input.v2Result));
  } else {
    if (input.schema === 'unknown') {
      lines.push(
        '> kiosk_state schema is unknown — report shows only fields recognized by the v1 fallback. Some data may be missing.',
      );
    } else {
      lines.push(
        '> This device sent kiosk_state v1. Full 14-section detail is available after it reports kiosk_state v2.',
      );
    }
    lines.push('');
    lines.push(...buildFullSnapshotV1(input.latestState));
  }
  lines.push('');
  lines.push(...buildHistorySection(input.rows));
  lines.push(...buildFooter());
  return lines.join('\n');
}

// ─── Helpers ────────────────────────────────────────────────────────────

function fmtUtc(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), "yyyy-MM-dd HH:mm:ss 'UTC'");
  } catch {
    return iso;
  }
}

function fmtRelative(iso?: string | null, generatedAt?: Date): string {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const now = (generatedAt ?? new Date()).getTime();
    const ms = now - t;
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    return `${day}d ago`;
  } catch {
    return iso;
  }
}

function schemaTag(schema: KioskSchema): string {
  if (schema === 'v2') return 'v2';
  if (schema === 'v1') return 'v1';
  if (schema === 'unknown') return '?';
  return 'none';
}

function statusSymbol(label: KioskHealthLabel): string {
  if (label === 'healthy') return '✓';
  if (label === 'no_snapshot') return '·';
  if (label === 'snapshot_incomplete') return '⊘';
  if (label === 'back_gesture_drifted' || label === 'kiosk_drift_detected') return '⚠';
  if (label === 'snapshot_unknown_schema') return '?';
  return '✗';
}

// ─── Section builders ───────────────────────────────────────────────────

function buildHeader(input: KioskHealthReportInput): string[] {
  return [
    '# Vortex Device Kiosk Health Report',
    `Generated: ${fmtUtc(input.generatedAt.toISOString())} · kiosk_state ${schemaTag(input.schema)}`,
  ];
}

function buildDeviceBlock(input: KioskHealthReportInput): string[] {
  const lines: string[] = ['## Device', `- ID: ${input.deviceId}`];
  if (input.device?.name) lines.push(`- Name: ${input.device.name}`);
  if (input.device?.serial_number) lines.push(`- Serial: ${input.device.serial_number}`);
  const vessel =
    input.latestCheckRow?.vesselName ??
    input.latestSnapshotRow?.vesselName ??
    null;
  if (vessel) lines.push(`- Vessel: ${vessel}`);
  return lines;
}

function buildLatestCheckBlock(input: KioskHealthReportInput): string[] {
  const r = input.latestCheckRow;
  if (!r) {
    return ['## Latest check', '- No update checks recorded yet.'];
  }
  const lines: string[] = ['## Latest check'];
  lines.push(
    `- When: ${fmtRelative(r.checkedAt, input.generatedAt)} (${fmtUtc(r.checkedAt)})`,
  );
  if (r.result) lines.push(`- Result: ${r.result.replace(/_/g, ' ')}`);
  if (typeof r.deviceMeta?.launcher_version === 'string') {
    lines.push(`- Launcher: ${r.deviceMeta.launcher_version}`);
  }
  if (typeof r.update_channel === 'string') {
    lines.push(`- Update channel: ${r.update_channel}`);
  }
  if (input.isStaleSnapshot) {
    lines.push(
      `- Stale snapshot: latest check did not include kiosk_state (snapshot shown below is from an earlier check).`,
    );
  }
  return lines;
}

function buildLatestSnapshotBlock(input: KioskHealthReportInput): string[] {
  if (input.latestSnapshotRow == null && input.latestCheckRow == null) return [];
  const r = input.latestSnapshotRow ?? input.latestCheckRow;
  if (!r) return [];
  const lines: string[] = ['## Latest kiosk snapshot'];
  lines.push(
    `- When: ${fmtRelative(r.checkedAt, input.generatedAt)} (${fmtUtc(r.checkedAt)})`,
  );
  lines.push(`- Schema: kiosk_state ${schemaTag(input.schema)}`);
  if (typeof r.deviceMeta?.launcher_version === 'string') {
    lines.push(`- Launcher: ${r.deviceMeta.launcher_version}`);
  }
  return lines;
}

function buildStatusSection(input: KioskHealthReportInput): string[] {
  const labelText = healthLabelText(input.health.primary);
  const symbol = statusSymbol(input.health.primary);
  const secondary = input.health.issues.filter((l) => l !== input.health.primary);
  const lines: string[] = [`## Status: ${symbol} ${labelText}`];
  if (secondary.length > 0) {
    lines.push(`Also: ${secondary.map((l) => healthLabelText(l)).join(' · ')}`);
  }
  if (input.isStaleSnapshot) {
    lines.push(
      'Note: the latest update check did not include kiosk_state; this status reflects the older snapshot above.',
    );
  }
  return lines;
}

function buildIssuesSection(input: KioskHealthReportInput): string[] {
  if (input.schema !== 'v2' || !input.v2Result) {
    // For v1/unknown rows, derive a flat-field issues list from v1 displays.
    if (!input.latestState) return [];
    const displays = kioskFieldDisplays(input.latestState);
    const issues = displays.filter(
      (f) => f.tone === 'warn' || f.tone === 'fail' || f.tone === 'unreadable',
    );
    if (issues.length === 0) return [];
    const lines: string[] = ['## Issues'];
    for (const f of issues) {
      const severity = f.tone === 'fail' ? 'Critical' : f.tone === 'warn' ? 'Warning' : 'Unreadable';
      lines.push(`- ${f.label}: ${f.value} (expected ${f.expected}) — ${severity}`);
    }
    return lines;
  }
  const lines: string[] = [];
  for (const section of input.v2Result.sections) {
    const sectionIssues = section.fields.filter(
      (f) => f.tone === 'warn' || f.tone === 'fail' || f.tone === 'unreadable',
    );
    if (sectionIssues.length === 0) continue;
    for (const f of sectionIssues) {
      const severity =
        f.tone === 'fail' ? 'Critical' : f.tone === 'warn' ? 'Warning' : 'Unreadable';
      lines.push(`- [${section.id}] ${f.label}: ${f.value} (expected ${f.expected}) — ${severity}`);
    }
  }
  if (lines.length === 0) return [];
  return ['## Issues', ...lines];
}

function runtimeDriftStatusText(rd: RuntimeDriftResult): string {
  switch (rd.status) {
    case 'fail':
      return 'Gesture desync suspected';
    case 'warn':
      return 'Drift detected';
    case 'incomplete':
      return rd.insetsCaptured ? 'Partial snapshot' : 'Insets not captured';
    default:
      return 'In sync';
  }
}

function buildRuntimeDriftSection(input: KioskHealthReportInput): string[] {
  const rd = computeRuntimeDrift(input.latestState);
  if (!rd.present) return [];
  const symbol =
    rd.status === 'ok' ? '✓' : rd.status === 'fail' ? '✗' : rd.status === 'warn' ? '⚠' : '·';
  const lines: string[] = [`## Runtime drift: ${symbol} ${runtimeDriftStatusText(rd)}`];
  lines.push(rd.summary);
  for (const f of rd.fields) {
    const mark =
      f.tone === 'ok' ? ' ✓' : f.tone === 'fail' ? ' ✗' : f.tone === 'warn' ? ' ⚠' : f.tone === 'info' ? '' : ' ?';
    const note = f.note ? ` — ${f.note}` : '';
    lines.push(`- ${f.label}: stored ${f.stored} · runtime ${f.runtime}${mark}${note}`);
  }
  return lines;
}

function buildChangesSection(input: KioskHealthReportInput): string[] {
  const c = input.compare;
  if (!c || !c.comparable || c.totalChanged === 0) return [];
  const lines: string[] = ['## Changes since previous snapshot'];
  if (!c.schemaMatch) {
    lines.push(
      '> Previous snapshot used a different kiosk_state schema; only shared v1 fields can be compared.',
    );
  }
  for (const ch of c.changes) {
    const sectionTag = ch.section ?? 'v1';
    lines.push(`- [${sectionTag}] ${ch.label}: ${ch.previousValue} → ${ch.currentValue}`);
  }
  return lines;
}

function statusLabel(s: KioskV2SectionResult['status']): string {
  switch (s) {
    case 'ok':
      return 'OK';
    case 'info':
      return 'Info';
    case 'warn':
      return 'Warning';
    case 'fail':
      return 'Critical';
    case 'incomplete':
      return 'Incomplete';
  }
}

function buildSectionSummaryTable(
  v2: KioskHealthV2Result,
  compare: KioskCompareResult | null,
): string[] {
  const lines: string[] = [
    '## Section summary',
    '',
    '| Section | Status | Issues | Changed |',
    '|---|---|---|---|',
  ];
  for (const s of v2.sections) {
    const changed =
      compare?.sectionChangedCounts?.[s.id] ?? s.changedFields ?? 0;
    lines.push(`| ${s.title} | ${statusLabel(s.status)} | ${s.issueCount} | ${changed} |`);
  }
  return lines;
}

function appGroupLabel(
  group: AppPermissionRow['groups'][number],
): string {
  if (group.requested === false || group.requested === null) {
    return `${group.label.toLowerCase()} not requested`;
  }
  if (group.granted === true) return `${group.label.toLowerCase()} granted`;
  if (group.granted === false) return `${group.label.toLowerCase()} denied`;
  return `${group.label.toLowerCase()} unreadable`;
}

function buildAppPermissionsSection(v2: KioskHealthV2Result): string[] {
  const apps = v2.sections.find((s) => s.id === 'app_permissions');
  if (!apps || apps.special?.kind !== 'app_permissions') return [];
  const lines: string[] = ['## App permissions'];
  for (const app of apps.special.apps) {
    const installed = app.installed === null ? 'unreadable' : app.installed ? 'installed' : 'not installed';
    const groupSummary = app.groups.map(appGroupLabel).join(' · ');
    lines.push(`- ${app.label}: ${installed} · ${groupSummary}`);
  }
  return lines;
}

function pkgLine(row: PackagePolicyRow): string {
  const installed = row.installed === null ? 'unreadable' : row.installed ? 'installed' : 'not installed';
  if (row.installed !== true) {
    return `- ${row.pkg}: ${installed}`;
  }
  const hidden =
    row.hidden === null ? 'unreadable' : row.hidden ? 'hidden' : 'unhidden';
  const expected =
    row.expectedHidden === 'either'
      ? '—'
      : row.expectedHidden
        ? 'hidden if installed'
        : 'unhidden if installed';
  const mark =
    row.status === 'ok' ? ' ✓' : row.status === 'fail' ? ' ✗' : row.status === 'warn' ? ' ⚠' : '';
  return `- ${row.pkg}: ${installed} · ${hidden} (expected ${expected})${mark}`;
}

function buildPackagePolicySection(v2: KioskHealthV2Result): string[] {
  const pkgSection = v2.sections.find((s) => s.id === 'package_policy');
  if (!pkgSection || pkgSection.special?.kind !== 'package_policy') return [];
  const lines: string[] = ['## Package policy'];
  for (const row of pkgSection.special.packages) {
    lines.push(pkgLine(row));
  }
  return lines;
}

function buildFullSnapshotV2(v2: KioskHealthV2Result): string[] {
  const lines: string[] = ['## Full snapshot'];
  for (const section of v2.sections) {
    lines.push('');
    lines.push(`### ${section.title} [${statusLabel(section.status)}]`);
    if (section.isIncomplete) {
      lines.push('> Section read incomplete — one or more reads failed.');
    }
    if (section.special?.kind === 'app_permissions') {
      for (const app of section.special.apps) {
        const installed = app.installed === null ? 'unreadable' : app.installed ? 'installed' : 'not installed';
        const groupSummary = app.groups.map(appGroupLabel).join(' · ');
        lines.push(`- ${app.label}: ${installed} · ${groupSummary}`);
      }
    } else if (section.special?.kind === 'package_policy') {
      for (const row of section.special.packages) {
        lines.push(pkgLine(row));
      }
    } else {
      for (const f of section.fields) {
        const mark =
          f.tone === 'ok' ? ' ✓' : f.tone === 'fail' ? ' ✗' : f.tone === 'warn' ? ' ⚠' : f.tone === 'info' ? '' : ' ?';
        const expected = f.expected === '—' ? '' : ` (expected ${f.expected})`;
        lines.push(`- ${f.label}: ${f.value}${expected}${mark}`);
      }
    }
  }
  return lines;
}

function buildFullSnapshotV1(state: KioskStateAny | null): string[] {
  const lines: string[] = ['## Full snapshot (v1 fields)'];
  const displays = kioskFieldDisplays(state);
  if (displays.length === 0) {
    lines.push('- (no fields available)');
    return lines;
  }
  for (const f of displays) {
    const mark =
      f.tone === 'ok' ? ' ✓' : f.tone === 'fail' ? ' ✗' : f.tone === 'warn' ? ' ⚠' : ' ?';
    lines.push(`- ${f.label}: ${f.value} (expected ${f.expected})${mark}`);
  }
  return lines;
}

function buildNoSnapshotBody(input: KioskHealthReportInput): string[] {
  const lines: string[] = ['## Status: No kiosk snapshot'];
  if (input.rows.length === 0) {
    lines.push(
      'No update checks have been recorded for this device yet.',
      '',
      'Ask the customer to open Launcher → Updates → Check All on the tablet. A snapshot will appear here on the next check.',
    );
  } else {
    lines.push(
      `${input.rows.length} update check${input.rows.length === 1 ? '' : 's'} on record, but no kiosk_state telemetry has been reported yet.`,
      '',
      'Ask the customer to open Launcher → Updates → Check All on the tablet, or wait for the next OTA wave to upgrade the launcher. A snapshot will appear here on the next check.',
    );
  }
  return lines;
}

function buildHistorySection(rows: KioskHealthReportRow[]): string[] {
  const lines: string[] = ['## Recent update checks'];
  if (rows.length === 0) {
    lines.push('- (no rows on record)');
    return lines;
  }
  const limit = Math.min(rows.length, 10);
  for (let i = 0; i < limit; i++) {
    const row = rows[i];
    const ks =
      row.deviceMeta && typeof row.deviceMeta === 'object' && row.deviceMeta.kiosk_state
        ? row.deviceMeta.kiosk_state
        : null;
    const sch = detectKioskSchema(ks);
    const health = computeKioskHealth(ks);
    const when = row.checkedAt ? fmtUtc(row.checkedAt) : '—';
    const launcher =
      typeof row.deviceMeta?.launcher_version === 'string' ? row.deviceMeta.launcher_version : null;
    const result = row.result ? row.result.replace(/_/g, ' ') : null;
    const parts = [when, `[${schemaTag(sch)}]`, healthLabelText(health.primary)];
    if (launcher) parts.push(`launcher ${launcher}`);
    if (result) parts.push(result);
    lines.push(`- ${parts.join(' · ')}`);
  }
  return lines;
}

function buildFooter(): string[] {
  return [
    '',
    '---',
    'Report generated by Vortex Marine Admin → Devices → Health tab',
  ];
}
